import { useEffect, useState } from "react";
import { Sparkles, Loader2, Save, Trash2, FileText, MapPin, HelpCircle, Layers, Eye, CheckCircle2, EyeOff, X, FileEdit, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Keywords from "@/pages/admin/Keywords";

const TYPES = [
  { id: "article", label: "Article conseil", icon: FileText },
  { id: "local", label: "Page locale (métier + ville)", icon: MapPin },
  { id: "faq", label: "FAQ", icon: HelpCircle },
];

const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

export default function SeoTools() {
  const [view, setView] = useState("seo");
  const [tab, setTab] = useState("single");
  const [services, setServices] = useState([]);
  const [list, setList] = useState([]);
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/content", { params: { published_only: false } }).then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/services").then((r) => setServices(r.data)); }, []);

  const drafts = list.filter((c) => !c.published);
  const published = list.filter((c) => c.published);

  const setPub = async (c, published) => {
    await api.put(`/content/${c.id}/publish`, { published });
    toast.success(published ? "Publié" : "Repassé en brouillon");
    load();
  };
  const remove = async (id) => { if (!window.confirm("Supprimer définitivement ?")) return; await api.delete(`/content/${id}`); load(); };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
        <button onClick={() => setView("seo")} data-testid="seo-view-content"
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${view === "seo" ? "border-brand text-brand" : "border-transparent text-stone hover:text-forest"}`}><FileText size={15} /> Contenus & génération IA</button>
        <button onClick={() => setView("keywords")} data-testid="seo-view-keywords"
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${view === "keywords" ? "border-brand text-brand" : "border-transparent text-stone hover:text-forest"}`}><TrendingUp size={15} /> Suivi mots-clés</button>
      </div>

      {view === "keywords" ? <Keywords /> : (
      <>
      <h1 className="font-display text-4xl text-forest mb-1">Outil SEO</h1>
      <p className="text-stone mb-6">Générez des pages et articles optimisés avec l'IA, puis validez les brouillons à publier.</p>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("single")} data-testid="tab-single"
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === "single" ? "bg-brand text-cream" : "bg-white border border-border text-stone hover:border-brand"}`}>Génération unique</button>
        <button onClick={() => setTab("batch")} data-testid="tab-batch"
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "batch" ? "bg-brand text-cream" : "bg-white border border-border text-stone hover:border-brand"}`}><Layers size={15} /> Génération en masse</button>
      </div>

      {tab === "single" ? <SingleGenerator onDone={load} /> : <BatchGenerator services={services} onDone={load} />}

      {/* Drafts */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <FileEdit className="text-brand" size={20} />
          <h3 className="font-semibold text-forest">Brouillons à valider ({drafts.length})</h3>
        </div>
        <ContentList items={drafts} onPreview={setPreview} onPublish={(c) => setPub(c, true)} onRemove={remove} draft />
      </div>

      {/* Published */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="text-green-600" size={20} />
          <h3 className="font-semibold text-forest">Contenus publiés ({published.length})</h3>
        </div>
        <ContentList items={published} onPreview={setPreview} onUnpublish={(c) => setPub(c, false)} onRemove={remove} />
      </div>

      {preview && <PreviewModal content={preview} onClose={() => setPreview(null)} />}
      </>
      )}
    </div>
  );
}

const TYPE_LABEL = { article: "Article", local: "Page locale", faq: "FAQ" };

function ContentList({ items, onPreview, onPublish, onUnpublish, onRemove, draft }) {
  if (items.length === 0) return <div className="rounded-xl bg-white border border-dashed border-border py-8 text-center text-sm text-stone">Aucun contenu.</div>;
  return (
    <div className="rounded-xl bg-white border border-border divide-y divide-border">
      {items.map((c) => (
        <div key={c.id} className="flex items-center gap-3 px-5 py-3" data-testid={`content-row-${c.id}`}>
          {c.cover_image && <img src={c.cover_image} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-forest truncate">{c.title}</p>
            <p className="text-xs text-stone">
              <span className="rounded bg-sage px-1.5 py-0.5 text-[10px] font-semibold text-forest mr-1.5">{TYPE_LABEL[c.type] || c.type}</span>
              {c.ville ? `${c.ville} · ` : ""}/{c.slug}
            </p>
          </div>
          <button onClick={() => onPreview(c)} data-testid={`preview-${c.id}`} className="text-stone hover:text-brand hover:bg-sage rounded-lg p-2" title="Aperçu"><Eye size={16} /></button>
          {draft && <button onClick={() => onPublish(c)} data-testid={`publish-${c.id}`} className="rounded-full bg-brand text-cream text-xs font-semibold px-3 py-1.5 hover:bg-forest flex items-center gap-1"><CheckCircle2 size={14} /> Publier</button>}
          {onUnpublish && <button onClick={() => onUnpublish(c)} data-testid={`unpublish-${c.id}`} className="text-stone hover:text-brand hover:bg-sage rounded-lg p-2" title="Dépublier"><EyeOff size={16} /></button>}
          <button onClick={() => onRemove(c.id)} data-testid={`delete-${c.id}`} className="text-red-500 hover:bg-red-50 rounded-lg p-2"><Trash2 size={16} /></button>
        </div>
      ))}
    </div>
  );
}

function PreviewModal({ content, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="preview-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <h3 className="font-display text-2xl text-forest pr-4">{content.title}</h3>
          <button onClick={onClose} className="text-stone hover:text-forest"><X size={22} /></button>
        </div>
        {content.cover_image && <img src={content.cover_image} alt="" className="w-full aspect-[16/8] object-cover" />}
        <div className="p-6">
          {content.excerpt && <p className="text-stone italic mb-4">{content.excerpt}</p>}
          <div className="prose-acanthe" dangerouslySetInnerHTML={{ __html: content.body }} />
          {content.keywords?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">{content.keywords.map((k) => <span key={k} className="rounded-full bg-sage px-3 py-1 text-xs text-forest">{k}</span>)}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SingleGenerator({ onDone }) {
  const [type, setType] = useState("article");
  const [gen, setGen] = useState({ topic: "", metier: "", ville: "", code_postal: "", keywords: "" });
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/seo/generate", { type, ...gen });
      setDraft({ ...data, type, metier: gen.metier, ville: gen.ville, code_postal: gen.code_postal, keywords: data.keywords || [] });
      toast.success("Contenu généré par l'IA");
    } catch { toast.error("Échec de la génération. Réessayez."); }
    finally { setGenerating(false); }
  };

  const save = async (published) => {
    setSaving(true);
    try {
      await api.post("/content", { ...draft, published, keywords: Array.isArray(draft.keywords) ? draft.keywords : String(draft.keywords).split(",").map((s) => s.trim()) });
      toast.success(published ? "Publié" : "Enregistré en brouillon");
      setDraft(null); onDone();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
      <div className="rounded-xl bg-white border border-border p-6 h-fit" data-testid="seo-generator">
        <h3 className="font-semibold text-forest mb-4 flex items-center gap-2"><Sparkles size={18} className="text-brand" /> Générateur IA</h3>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {TYPES.map((t) => (
            <button key={t.id} onClick={() => setType(t.id)} data-testid={`seo-type-${t.id}`}
              className={`rounded-lg border p-3 text-xs font-medium flex flex-col items-center gap-1.5 transition-colors ${type === t.id ? "border-brand bg-brand/5 text-brand" : "border-border text-stone hover:border-brand"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          {type === "local" ? (
            <>
              <div><label className="text-xs text-stone">Métier / prestation</label><input className={input} placeholder="Ex: Élagage, Création de jardin" value={gen.metier} onChange={(e) => setGen({ ...gen, metier: e.target.value })} data-testid="gen-metier" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-stone">Ville</label><input className={input} placeholder="Aix-en-Provence" value={gen.ville} onChange={(e) => setGen({ ...gen, ville: e.target.value })} data-testid="gen-ville" /></div>
                <div><label className="text-xs text-stone">Code postal</label><input className={input} placeholder="13100" value={gen.code_postal} onChange={(e) => setGen({ ...gen, code_postal: e.target.value })} data-testid="gen-cp" /></div>
              </div>
            </>
          ) : (
            <div><label className="text-xs text-stone">Sujet</label><input className={input} placeholder="Ex: Entretenir son olivier en été" value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} data-testid="gen-topic" /></div>
          )}
          <div><label className="text-xs text-stone">Mots-clés cibles</label><input className={input} placeholder="paysagiste, jardin méditerranéen…" value={gen.keywords} onChange={(e) => setGen({ ...gen, keywords: e.target.value })} data-testid="gen-keywords" /></div>
          <button onClick={generate} disabled={generating} data-testid="seo-generate-btn"
            className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {generating ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />} {generating ? "Génération…" : "Générer le contenu"}
          </button>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-border p-6" data-testid="seo-draft">
        {!draft ? (
          <div className="text-center text-stone py-20"><Sparkles className="mx-auto mb-3 text-stone/40" size={32} /><p>Le contenu généré s'affichera ici pour révision.</p></div>
        ) : (
          <div className="space-y-3">
            <div><label className="text-xs text-stone">Titre</label><input className={input} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="draft-title" /></div>
            <div><label className="text-xs text-stone">Extrait</label><textarea rows={2} className={input} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-stone">SEO Titre</label><input className={input} value={draft.seo_title} onChange={(e) => setDraft({ ...draft, seo_title: e.target.value })} /></div>
              <div><label className="text-xs text-stone">SEO Description</label><input className={input} value={draft.seo_description} onChange={(e) => setDraft({ ...draft, seo_description: e.target.value })} /></div>
            </div>
            <div><label className="text-xs text-stone">Contenu (HTML)</label><textarea rows={10} className={`${input} font-mono text-xs`} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="draft-body" /></div>
            <div className="rounded-lg border border-border p-4 bg-[#FAF8F5] max-h-52 overflow-y-auto prose-acanthe" dangerouslySetInnerHTML={{ __html: draft.body }} />
            <div className="flex gap-3">
              <button onClick={() => setDraft(null)} className="rounded-full px-5 py-2.5 text-sm text-stone hover:bg-sage">Rejeter</button>
              <button onClick={() => save(false)} disabled={saving} data-testid="seo-savedraft-btn" className="rounded-full border border-brand text-brand py-2.5 px-5 text-sm font-semibold hover:bg-brand/5 flex items-center gap-2 disabled:opacity-60"><Save size={16} /> Brouillon</button>
              <button onClick={() => save(true)} disabled={saving} data-testid="seo-save-btn" className="flex-1 rounded-full bg-brand text-cream py-2.5 text-sm font-semibold hover:bg-forest flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Publier</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BatchGenerator({ services, onDone }) {
  const [type, setType] = useState("local");
  const [metier, setMetier] = useState("");
  const [villesText, setVillesText] = useState("Aix-en-Provence, 13100\nMarseille, 13000\nSalon-de-Provence, 13300");
  const [keywords, setKeywords] = useState("paysagiste, aménagement extérieur, devis gratuit");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });

  const parseVilles = () => villesText.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
    const parts = line.split(",").map((p) => p.trim());
    let ville = parts[0], cp = "";
    if (parts[1]) cp = parts[1];
    else { const m = ville.match(/(\d{5})/); if (m) { cp = m[1]; ville = ville.replace(m[1], "").trim(); } }
    return { ville, code_postal: cp };
  });

  const run = async () => {
    if (!metier) { toast.error("Choisissez un métier"); return; }
    const villes = parseVilles();
    if (villes.length === 0) { toast.error("Ajoutez au moins une ville"); return; }
    setRunning(true);
    setProgress({ done: 0, total: villes.length, current: "" });
    let ok = 0;
    for (let i = 0; i < villes.length; i++) {
      setProgress({ done: i, total: villes.length, current: villes[i].ville });
      try {
        await api.post("/seo/generate-draft", { type, metier, ville: villes[i].ville, code_postal: villes[i].code_postal, keywords });
        ok++;
        onDone();
      } catch { toast.error(`Échec pour ${villes[i].ville}`); }
    }
    setProgress({ done: villes.length, total: villes.length, current: "" });
    setRunning(false);
    toast.success(`${ok} brouillon(s) généré(s) — à valider ci-dessous`);
  };

  return (
    <div className="rounded-xl bg-white border border-border p-6" data-testid="batch-generator">
      <h3 className="font-semibold text-forest mb-1 flex items-center gap-2"><Layers size={18} className="text-brand" /> Génération en masse (ville × métier)</h3>
      <p className="text-sm text-stone mb-5">Sélectionnez un type et un métier, listez vos villes (une par ligne, format « Ville, CodePostal »). Chaque contenu généré arrive en brouillon.</p>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-stone">Type de contenu</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button onClick={() => setType("local")} data-testid="batch-type-local" className={`rounded-lg border p-3 text-xs font-medium flex items-center justify-center gap-1.5 ${type === "local" ? "border-brand bg-brand/5 text-brand" : "border-border text-stone hover:border-brand"}`}><MapPin size={15} /> Page locale</button>
              <button onClick={() => setType("article")} data-testid="batch-type-article" className={`rounded-lg border p-3 text-xs font-medium flex items-center justify-center gap-1.5 ${type === "article" ? "border-brand bg-brand/5 text-brand" : "border-border text-stone hover:border-brand"}`}><FileText size={15} /> Article de blog</button>
            </div>
          </div>
          <div>
            <label className="text-xs text-stone">Métier / prestation</label>
            <select className={input} value={metier} onChange={(e) => setMetier(e.target.value)} data-testid="batch-metier">
              <option value="">— Choisir un métier —</option>
              {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-stone">Mots-clés cibles</label>
            <input className={input} value={keywords} onChange={(e) => setKeywords(e.target.value)} data-testid="batch-keywords" />
          </div>
        </div>
        <div>
          <label className="text-xs text-stone">Villes (une par ligne — « Ville, CP »)</label>
          <textarea rows={7} className={`${input} font-mono text-xs`} value={villesText} onChange={(e) => setVillesText(e.target.value)} data-testid="batch-villes" />
          <p className="text-xs text-stone mt-1">{parseVilles().length} ville(s) · {parseVilles().length} contenu(s) seront générés</p>
        </div>
      </div>

      {running && (
        <div className="mt-5">
          <div className="flex justify-between text-xs text-stone mb-1"><span>Génération en cours… {progress.current && `(${progress.current})`}</span><span>{progress.done}/{progress.total}</span></div>
          <div className="h-2 rounded-full bg-sage overflow-hidden"><div className="h-full bg-brand transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} /></div>
        </div>
      )}

      <button onClick={run} disabled={running} data-testid="batch-run-btn"
        className="mt-5 w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
        {running ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />} {running ? "Génération…" : "Générer les brouillons"}
      </button>
    </div>
  );
}
